import {
  useCallback,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  DEFAULT_ROUTE,
  parseRouteHash,
  routeHash,
  type RouteId,
} from "./routes";

type NavigateOptions = {
  /** When true, push a history entry (default). When false, used for popstate sync. */
  push?: boolean;
  /** Scroll to top after navigation (default true for user navigations). */
  scroll?: boolean;
};

type RouterState = {
  route: RouteId;
  navigate: (id: RouteId, options?: NavigateOptions) => void;
};

let currentRoute: RouteId = DEFAULT_ROUTE;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function readRouteFromLocation(): RouteId {
  return parseRouteHash(window.location.hash || "");
}

function getSnapshot(): RouteId {
  return currentRoute;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function applyRoute(id: RouteId, push: boolean) {
  currentRoute = id;
  const nextHash = routeHash(id);
  if (push) {
    if (window.location.hash !== nextHash) {
      history.pushState({ route: id }, "", nextHash);
    }
  } else if (window.location.hash !== nextHash) {
    // Empty hash or unknown hash: establish the canonical route (default #field)
    // without an extra back entry. Valid explicit hashes already match nextHash.
    history.replaceState({ route: id }, "", nextHash);
  }
  emit();
}

function syncFromHistory() {
  const resolved = readRouteFromLocation();
  // Same-document hash jumps (e.g. unknown `#…`) do not remount React; canonicalize
  // empty/unknown hashes to the default route without a pushState entry.
  if (routeHash(resolved) !== window.location.hash) {
    applyRoute(resolved, false);
    return;
  }
  currentRoute = resolved;
  emit();
}

let installed = false;

function ensureListeners() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  currentRoute = readRouteFromLocation();
  window.addEventListener("popstate", syncFromHistory);
  window.addEventListener("hashchange", syncFromHistory);
}

export function useHashRoute(): RouterState {
  ensureListeners();
  const route = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_ROUTE);

  const navigate = useCallback((id: RouteId, options?: NavigateOptions) => {
    const push = options?.push !== false;
    const scroll = options?.scroll !== false;
    applyRoute(id, push);
    if (scroll) {
      const scroller = document.scrollingElement || document.documentElement;
      scroller.scrollTop = 0;
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, []);

  useEffect(() => {
    ensureListeners();
    // Empty / unknown hashes resolve to DEFAULT_ROUTE and canonicalize to #field.
    // Explicit valid routes (including #earth3) keep their hash unchanged.
    applyRoute(readRouteFromLocation(), false);
  }, []);

  return { route, navigate };
}

export function HashRouteProvider({ children }: { children: ReactNode }) {
  // Provider is a thin mount hook so listeners install once at app root
  useHashRoute();
  return children;
}

export function useRoute(): RouteId {
  return useHashRoute().route;
}
